/**
 * Background analysis, as the sidebar and status bar show it.
 *
 * Re-read when the engine reports progress (at most once a second while it is
 * working) and when the queue is changed -- never on a timer of its own.
 */

import { useCallback, useEffect, useState } from "react";
import { fetchJobs, pauseJobs, retryJobs, setJobWorkers, stopJobs, type JobReport } from "../api";
import { bus } from "./bus";

let cached: JobReport | null = null;

export function useJobs(offline: boolean) {
  const [report, setReport] = useState<JobReport | null>(cached);

  const refresh = useCallback(async () => {
    if (offline) return;
    const next = await fetchJobs();
    if (next) {
      cached = next;
      setReport(next);
    }
  }, [offline]);

  useEffect(() => {
    void refresh();
    let pending: number | null = null;
    const soon = () => {
      if (pending !== null) return;
      pending = window.setTimeout(() => {
        pending = null;
        void refresh();
      }, 1000);
    };
    const offs = [bus.on("analysis.*", soon), bus.on("library.changed", soon), bus.on("engine.resync", soon)];
    return () => {
      offs.forEach((off) => off());
      if (pending !== null) window.clearTimeout(pending);
    };
  }, [refresh]);

  const act = useCallback(
    async (fn: () => Promise<JobReport | null>) => {
      const next = await fn();
      if (next) {
        cached = { ...(cached ?? next), ...next };
        setReport(cached);
      }
      void refresh();
    },
    [refresh]
  );

  return {
    report,
    pause: () => act(() => pauseJobs(true)),
    resume: () => act(() => pauseJobs(false)),
    stop: () => act(stopJobs),
    retry: () => act(retryJobs),
    setWorkers: (n: number) => act(() => setJobWorkers(n)),
  };
}
